import type { DataLayer } from 'corgi-types';
// We are using library for serializing, instead of JSON.stringify in order
// to prevent XSS vulnerabilities. More details:
// https://pragmaticwebsecurity.com/articles/spasecurity/json-stringify-xss.html
import serialize from 'serialize-javascript';

type DataLayerShape = [DataLayer, ...object[]];

/**
 * Generates a data layer initialization script that supports client-side navigation.
 *
 * On the first page load (no `gtm.js` event yet), it initializes `window.dataLayer` with the
 * provided data, and re-appends any events that were pushed before initialization.
 *
 * On subsequent soft navigations (`gtm.js` already present), it pushes the new page's data layer
 * events individually without re-initializing GTM, preserving the existing event history.
 *
 * This ensures that the initial page data event (containing appName, country, language properties)
 * is always the first event in the data layer array. GTM depends on this behavior
 * to function correctly.
 *
 * More context: https://home24.atlassian.net/wiki/x/AQDgAAE
 */
export const getDataLayerScriptWithNavigationSupport = (dataLayer: DataLayerShape) => `
  (function() {
    const initialEvents = window.dataLayer || [];

    const isAlreadyInitialized = initialEvents.some(item => item.event === 'gtm.js');

    if (isAlreadyInitialized) {
      ${dataLayer
        .map(item => `window.dataLayer.push(${serialize(item, { isJSON: true })});`)
        .join('\n')}
    } else {
      window.dataLayer = ${serialize(dataLayer || [], { isJSON: true })};
      window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      initialEvents.forEach((event) => {
        window.dataLayer.push(event);
      });
    }

  })();
`;
